import { differenceInMilliseconds } from 'date-fns';
import type { EnhancedAppointment } from '@bb/domain/dist/types';
import type { AppointmentStatusEventEntry, QueuePreferences } from './types';

type FlagUpdateArgs = { appointmentId: string; flagged: boolean };

export const applyFlaggedUpdateToAnsweringCache = (
  old: any,
  { appointmentId, flagged }: FlagUpdateArgs,
) => {
  if (!old?.pages?.length) return old;

  let found = false;
  const nextPages = old.pages.map((page: any) => {
    if (!page?.data?.length) return page;
    const nextData = page.data.map((row: any) => {
      if (row?.appointment?.id !== appointmentId) return row;
      found = true;
      return {
        ...row,
        appointment: { ...row.appointment, flagged },
      };
    });
    return { ...page, data: nextData };
  });

  if (!found) return old;
  return { ...old, pages: nextPages };
};

export const applyStatusUpdateToAnsweringCache = (
  old: any,
  entry: AppointmentStatusEventEntry,
  queuePreferences: QueuePreferences,
) => {
  if (!old?.pages?.length) return old;

  const updatedAppointment = entry.appointment;
  if (!updatedAppointment?.id) return old;

  const { appointmentPreferences, groupPreferences } = queuePreferences || {};

  // Determine if the appointment should be visible based on preferences.
  // Default to keeping it if preferences haven't loaded yet.
  let shouldRemove = false;

  if (updatedAppointment.isDeleted) {
    shouldRemove = true;
  } else if (updatedAppointment.isPending === false) {
    shouldRemove = true;
  } else if (appointmentPreferences) {
    const isAllowedByStatus =
      appointmentPreferences[updatedAppointment.status] !== false;

    if (!isAllowedByStatus) {
      shouldRemove = true;
    }
  }

  if (groupPreferences && !shouldRemove) {
    const { orgId } = updatedAppointment;
    const locationIndex = Number(updatedAppointment.listing?.location);

    const allGroupPrefs = Object.values(groupPreferences) as Array<
      Record<string, boolean[]>
    >;

    const orgIsInAnyGroupPrefs = allGroupPrefs.some(
      preference => !!preference?.[orgId],
    );

    if (orgIsInAnyGroupPrefs) {
      let shouldAdd = false;
      allGroupPrefs.forEach(preference => {
        if (preference?.[orgId]) {
          shouldAdd = !!preference[orgId][locationIndex];
        }
      });
      if (!shouldAdd) {
        shouldRemove = true;
      }
    }
  }

  const updatedEntry = {
    listing: updatedAppointment.listing,
    appointment: {
      ...updatedAppointment,
      urgency: getUrgency(updatedAppointment),
    },
  };

  // Check if the appointment already exists in any page
  const existsInCache = old.pages.some((page: any) =>
    page?.data?.some(
      (row: any) => row?.appointment?.id === entry.appointmentId,
    ),
  );

  const totalBefore = old.pages.reduce(
    (sum: number, page: any) => sum + (page?.data?.length ?? 0),
    0,
  );

  let nextPages: any[];

  if (existsInCache) {
    if (shouldRemove) {
      // Remove the appointment from whichever page it's on
      nextPages = old.pages.map((page: any) => {
        if (!page?.data) return page;
        const filteredData = page.data.filter(
          (row: any) => row?.appointment?.id !== entry.appointmentId,
        );
        return { ...page, data: filteredData };
      });
    } else {
      // Update the appointment in place
      nextPages = old.pages.map((page: any) => {
        if (!page?.data) return page;
        const nextData = page.data.map((row: any) => {
          if (row?.appointment?.id !== entry.appointmentId) return row;
          return updatedEntry;
        });
        return { ...page, data: nextData };
      });
    }
  } else if (!shouldRemove) {
    // New appointment that should be in the queue — add to first page
    nextPages = old.pages.map((page: any, pageIndex: number) => {
      if (pageIndex !== 0 || !page?.data) return page;
      return { ...page, data: [...page.data, updatedEntry] };
    });
  } else {
    return old;
  }

  const totalAfter = nextPages.reduce(
    (sum: number, page: any) => sum + (page?.data?.length ?? 0),
    0,
  );
  const countDiff = totalAfter - totalBefore;

  // Adjust maxCount on all pages so getNextPageParam stays consistent
  const adjustedPages = nextPages.map((page: any) => ({
    ...page,
    maxCount: Math.max(0, (page.maxCount ?? 0) + countDiff),
  }));

  return { ...old, pages: adjustedPages };
};

export const getUrgency = (appointment: EnhancedAppointment) => {
  const now = new Date();
  const notice = appointment.notice || 0;
  if (appointment.status === 'time_passed') return 0;
  if (appointment.flagged) return 1;
  if (
    differenceInMilliseconds(new Date(appointment.time), now) <
    (3 + notice) * 60 * 60 * 1000
  )
    return 1;
  return 10;
};

export const calculateTimeThreshold = (
  hours: number,
  notice?: number,
  noticeMinutes?: number,
) =>
  Date.now() +
  hours * 60 * 60 * 1000 +
  ((notice ?? 0) * 60 + (noticeMinutes ?? 0)) * 60 * 1000;
